import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import Modal from "react-bootstrap/Modal"
import { useNavigate } from "react-router-dom"

/**
 * @typedef {{
 *   isBlocked: boolean,
 *   message?: string,
 * }} UnsavedChangesBlocker
 */

/**
 * @typedef {{
 *   setBlocker: (blocker: UnsavedChangesBlocker | null) => void,
 *   requestNavigate: (to: string) => void,
 * }} UnsavedChangesContextValue
 */

/** @type {React.Context<UnsavedChangesContextValue | null>} */
const UnsavedChangesContext = createContext(null)

const DEFAULT_MESSAGE =
    "Are you sure you want to leave this page? Any unsaved order details will be lost."

/**
 * Provides in-app leave confirmation + native beforeunload when a page registers as blocked.
 * Works with BrowserRouter (no data-router useBlocker required).
 * @param {{ children: React.ReactNode }} props
 */
export function UnsavedChangesProvider({ children }) {
    const navigate = useNavigate()
    const [blocker, setBlockerState] = useState(/** @type {UnsavedChangesBlocker | null} */(null))
    const [pendingTo, setPendingTo] = useState(/** @type {string | null} */(null))
    const bypassNextRef = useRef(false)

    const setBlocker = useCallback((next) => {
        setBlockerState(next)
    }, [])

    const requestNavigate = useCallback((to) => {
        const target = String(to ?? "")
        if (!target) {
            return
        }
        if (blocker?.isBlocked && !bypassNextRef.current) {
            setPendingTo(target)
            return
        }
        bypassNextRef.current = false
        navigate(target)
    }, [blocker?.isBlocked, navigate])

    useEffect(() => {
        if (!blocker?.isBlocked) {
            return
        }
        /** @param {BeforeUnloadEvent} event */
        function onBeforeUnload(event) {
            event.preventDefault()
            event.returnValue = ""
        }
        window.addEventListener("beforeunload", onBeforeUnload)
        return () => window.removeEventListener("beforeunload", onBeforeUnload)
    }, [blocker?.isBlocked])

    const value = useMemo(
        () => ({ setBlocker, requestNavigate }),
        [setBlocker, requestNavigate]
    )

    function stay() {
        setPendingTo(null)
    }

    function leave() {
        const to = pendingTo
        setPendingTo(null)
        if (!to) {
            return
        }
        bypassNextRef.current = true
        setBlockerState(null)
        navigate(to)
    }

    return (
        <UnsavedChangesContext.Provider value={value}>
            {children}
            <Modal show={pendingTo != null} onHide={stay} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Leave page?</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {blocker?.message ?? DEFAULT_MESSAGE}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="btn btn-outline-secondary" onClick={stay}>
                        Stay
                    </button>
                    <button type="button" className="btn btn-danger" onClick={leave}>
                        Leave
                    </button>
                </Modal.Footer>
            </Modal>
        </UnsavedChangesContext.Provider>
    )
}

/**
 * Register whether leaving the current page should prompt.
 * Clears on unmount.
 * @param {boolean} isBlocked
 * @param {string} [message]
 */
export function useUnsavedChangesBlocker(isBlocked, message = DEFAULT_MESSAGE) {
    const ctx = useContext(UnsavedChangesContext)

    useEffect(() => {
        if (!ctx) {
            return
        }
        if (isBlocked) {
            ctx.setBlocker({ isBlocked: true, message })
        } else {
            ctx.setBlocker(null)
        }
        return () => ctx.setBlocker(null)
    }, [ctx, isBlocked, message])
}

/**
 * Navigate with unsaved-changes confirmation when a blocker is active.
 * Falls back to react-router navigate when provider is absent.
 */
export function useRequestNavigate() {
    const ctx = useContext(UnsavedChangesContext)
    const navigate = useNavigate()

    return useCallback((to) => {
        if (ctx) {
            ctx.requestNavigate(to)
            return
        }
        navigate(to)
    }, [ctx, navigate])
}
